import { List } from 'antd'

import * as R from 'ramda'

import React from 'react'
import { connect } from 'react-redux'
import { firestoreConnect, isLoaded, withFirebase, withFirestore } from 'react-redux-firebase'
import { withRouter } from 'react-router'
import { compose, withHandlers, withProps } from 'recompose'
import {
  DEFAULT_CYCLE_TIMEOUT,
  DEFAULT_DISPLAY_MODE,
  DEFAULT_TEAMS_PER_PAGE,
  DISPLAY_MODE_TV,
} from '../../constants/displayMode'
import { isAdmin } from '../../helpers/auth'
import { calculateNextPage } from '../../helpers/category-results'
import {
  dataFromOrdered,
  findInOrdered,
  fsCollections,
  makeInBatchWithFirebase,
} from '../../helpers/firestore'
import { teamResults } from '../../helpers/results'
import { parse } from '../../helpers/url'

import { Flight } from '../../schemas/Flight'
import { TournamentPlayer } from '../../schemas/Player'
import { Playground } from '../../schemas/Playground'
import { CombinedResults, TeamResult } from '../../schemas/Results'
import { GlobalTeam, TeamId } from '../../schemas/Team'
import { Tournament } from '../../schemas/Tournament'

import * as notificationActions from '../../actions/notifications'

import { TeamResultItem } from './TeamResultItem'

import './TeamResultsList.css'

type PlayoffHandlers = {
  setPlayoffFlag(teamIds: number[]): (playoffFlag: boolean) => void
  setPlayoffRank(teamId: number): (playoffRank: number) => void
}

type TeamResultsListOuterProps = {
  playground: Playground
  tenant: string
  tournamentId: string
}

type TeamResultsListInnerProps = TeamResultsListOuterProps &
  PlayoffHandlers & {
    tournament: Tournament
    tournamentPlayers: TournamentPlayer[]
    tournamentFlights: Flight[]
    tournamentCombinedResultsMap: { [id: string]: CombinedResults }
    tournamentTeams: any[]
    tournamentTeamsMap: any
    globalTeamsMap: Record<TeamId, GlobalTeam>
    tenant: string
    profile: any
    displayMode: 'tv' | 'pc'
    cycleTimeout: number
    teamsPerPage: number
  }

export type TeamResultItemProps = {
  teamResult: TeamResult
  tournament: Tournament
  admin: boolean
  largeFlag: boolean
} & PlayoffHandlers

type TeamResultsListState = {
  currentPage: number
}

class TeamResultsList extends React.Component<TeamResultsListInnerProps, TeamResultsListState> {
  state = {
    currentPage: 1,
  }

  private cycleInterval: number

  loaded = (): boolean => {
    const {
      tournament,
      tournamentPlayers,
      tournamentFlights,
      tournamentTeams,
      globalTeamsMap,
      tournamentCombinedResultsMap,
    } = this.props

    return isLoaded(
      tournament,
      tournamentPlayers,
      tournamentFlights,
      tournamentTeams,
      globalTeamsMap,
      tournamentCombinedResultsMap,
    )
  }

  teams = () => {
    const {
      tournament,
      tournamentPlayers,
      tournamentTeamsMap,
      globalTeamsMap,
      tournamentCombinedResultsMap,
    } = this.props

    return this.loaded()
      ? teamResults(
          tournament,
          tournamentPlayers,
          tournamentCombinedResultsMap,
          globalTeamsMap,
          tournamentTeamsMap,
        )
      : []
  }

  cyclePages = () => {
    this.setState((state) => ({
      currentPage: calculateNextPage(
        this.teams().length,
        this.props.teamsPerPage,
        state.currentPage,
      ),
    }))
  }

  componentDidMount() {
    if (this.props.displayMode.toLowerCase() === DISPLAY_MODE_TV) {
      this.cycleInterval = window.setInterval(this.cyclePages, this.props.cycleTimeout)
    }
  }

  componentWillUnmount() {
    if (this.cycleInterval) {
      window.clearInterval(this.cycleInterval)
    }
  }

  // https://medium.freecodecamp.org/react-pattern-extract-child-components-to-avoid-binding-e3ad8310725e
  renderListItem(
    tournament: Tournament,
    admin: boolean,
    setPlayoffFlag: (teamIds: number[]) => (playoffFlag: boolean) => void,
    setPlayoffRank: (teamId: number) => (playoffRank: number) => void,
  ) {
    return (teamResult: TeamResult) => (
      <TeamResultItem
        key={teamResult.position}
        teamResult={teamResult}
        tournament={tournament}
        admin={admin}
        setPlayoffFlag={setPlayoffFlag}
        setPlayoffRank={setPlayoffRank}
        largeFlag={this.props.displayMode.toLowerCase() === DISPLAY_MODE_TV}
      />
    )
  }

  render() {
    const {
      displayMode,
      profile,
      teamsPerPage,
      tenant,
      tournament,
      setPlayoffFlag,
      setPlayoffRank,
    } = this.props

    const loadedNow = this.loaded()
    const currentTeams = this.teams()

    const resultItems =
      displayMode === DISPLAY_MODE_TV
        ? R.splitEvery(teamsPerPage, currentTeams)[this.state.currentPage - 1]
        : currentTeams

    return (
      <List
        className="TeamResultsList"
        itemLayout="vertical"
        size="default"
        loading={!loadedNow}
        dataSource={resultItems}
        renderItem={this.renderListItem(
          tournament,
          isAdmin(tenant, profile),
          setPlayoffFlag,
          setPlayoffRank,
        )}
      />
    )
  }
}

const addPropsFromQuery = ({ location }) => {
  const {
    teamsPerPage = DEFAULT_TEAMS_PER_PAGE,
    displayMode = DEFAULT_DISPLAY_MODE,
    cycleTimeout = DEFAULT_CYCLE_TIMEOUT,
  } = parse(location.search)

  // query param is a string
  const teamsPerPageParsed = isNaN(Number(teamsPerPage))
    ? DEFAULT_TEAMS_PER_PAGE
    : Number(teamsPerPage)

  return {
    teamsPerPage: teamsPerPageParsed,
    displayMode,
    cycleTimeout,
  }
}

export default compose<TeamResultsListInnerProps, TeamResultsListOuterProps>(
  withFirestore,
  withFirebase,
  withRouter,
  firestoreConnect<FIXME>(({ tenant, tournamentId }) => [
    { collection: fsCollections.tournaments(tenant), doc: tournamentId },
    { collection: fsCollections.tournamentPlayers(tenant, tournamentId) },
    { collection: fsCollections.tournamentCombinedResults(tenant, tournamentId) },
    { collection: fsCollections.tournamentFlights(tenant, tournamentId) },
    { collection: fsCollections.tournamentTeams(tenant, tournamentId) },
    { collection: fsCollections.globalTeams(tenant) },
  ]),
  connect(({ firestore, firebase: { profile } }: FIXME, { tenant, tournamentId }: FIXME) => ({
    tournament: findInOrdered(firestore.ordered[fsCollections.tournaments(tenant)], tournamentId),
    tournamentPlayers: firestore.ordered[fsCollections.tournamentPlayers(tenant, tournamentId)],
    tournamentCombinedResultsMap: dataFromOrdered(
      firestore.ordered[fsCollections.tournamentCombinedResults(tenant, tournamentId)],
    ),
    tournamentFlights: firestore.ordered[fsCollections.tournamentFlights(tenant, tournamentId)],
    tournamentTeams: firestore.ordered[fsCollections.tournamentTeams(tenant, tournamentId)],
    tournamentTeamsMap: dataFromOrdered(
      firestore.ordered[fsCollections.tournamentTeams(tenant, tournamentId)],
    ),
    globalTeamsMap: dataFromOrdered(firestore.ordered[fsCollections.globalTeams(tenant)]),
    profile,
  })),
  withProps(addPropsFromQuery),
  withHandlers<FIXME, FIXME>({
    onSetPlayoffFlagError: ({ dispatch }) => (err) => {
      const msg = 'Failed to set playoff flag'
      // tslint:disable-next-line:no-console
      console.error(msg, err)
      dispatch(notificationActions.notifyFailure(msg))
    },
    onSetPlayoffRankError: ({ dispatch }) => (err) => {
      const msg = 'Failed to set playoff rank'
      // tslint:disable-next-line:no-console
      console.error(msg, err)
      dispatch(notificationActions.notifyFailure(msg))
    },
    saveTeam: ({ firestore, tenant, tournamentId, tournamentTeamMap }) => (teamId: string) => (
      team: any /* TODO */,
    ) =>
      firestore.set(
        {
          collection: fsCollections.tournamentTeams(tenant, tournamentId),
          doc: String(teamId),
        },
        team,
      ),
    inBatchBound: makeInBatchWithFirebase,
  }),
  withHandlers({
    setPlayoffFlag: ({
      inBatchBound,
      tenant,
      tournamentId,
      tournamentTeamsMap,
      onSetPlayoffFlagError,
    }) => (teamIds: number[]) => (playoffFlag: boolean) => {
      inBatchBound((firestore, batch) => {
        const collection = firestore.collection(fsCollections.tournamentTeams(tenant, tournamentId))
        teamIds.forEach((teamId) => {
          const team = (tournamentTeamsMap && tournamentTeamsMap[teamId]) || { id: teamId }
          const updatedTeam = (R.compose(
            R.set(R.lensPath(['playoffRank', 'teams']), 1),
            R.set(R.lensPath(['playoffFlag', 'teams']), playoffFlag) as FIXME,
          ) as FIXME)(team)
          batch.set(collection.doc(String(teamId)), updatedTeam)
        })
      }).catch(onSetPlayoffFlagError)
    },
    setPlayoffRank: ({ saveTeam, categoryId, tournamentTeamsMap, onSetPlayoffRankError }) => (
      teamId: number,
    ) => (rank: number) => {
      const team = (tournamentTeamsMap && tournamentTeamsMap[teamId]) || { id: teamId }
      saveTeam(teamId)(R.set(R.lensPath(['playoffRank', 'teams']), rank, team)).catch(
        onSetPlayoffRankError,
      )
    },
  }),
)(TeamResultsList)



// WEBPACK FOOTER //
// ./src/components/tournament/TeamResultsList.tsx